import { FC, ReactNode } from "react";
import { APP_CONFIG } from "../../constants/constants";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { AdminFormFieldSubmit } from "./formFields";

type AdminFormFileInputProps = {
  id: string;
  label: string | ReactNode;
  onChange?: (file: File | null) => void;
  value?: any;
  fileName?: string | null;
  error?: string;
  showErrorValue?: boolean;
  disabled?: boolean;
  customInputClass?: string;
  customWrapperClass?: string;
  allowedFields?: string[];
  buttonSkin?: boolean;
};

export const AdminFormFileInput: FC<AdminFormFileInputProps> = ({
  id,
  label,
  onChange,
  value,
  fileName,
  error,
  allowedFields,
  disabled = false,
  showErrorValue = true,
  customInputClass = "",
  customWrapperClass = "col-span-6",
  buttonSkin = false,
}) => {
  const { t } = useTranslation();
  const fieldHasError = !!error;
  let wrapperClass = fieldHasError ? "" : "";
  let labelClass = fieldHasError
    ? "text-field-error-dark"
    : "text-field-label-valid";
  let inputClass = fieldHasError
    ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
    : "border-gray-200 text-field-label-valid";
  const floatingError = false;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("file", e.target.files);
    if (e.target.files) {
      if (onChange) {
        console.log("file", e.target.files[0]);
        onChange(e.target.files[0]);
      }
    }
  };

  if (buttonSkin) {
    return (
      <div className={`relative group ${wrapperClass} ${customWrapperClass}`}>
        <label
          htmlFor={id}
          className={`absolute top-0 left-0 right-0 bottom-0${disabled ? "" : " cursor-pointer"} z-[1]`}
        />
        <AdminFormFieldSubmit
          type="button"
          variant="primary-like"
          customWrapperClass="relative"
          buttonDimensionClass={
            "h-10 w-full px-4 group enabled:group-hover:opacity-80"
          }
          label={label}
          disabled={disabled}
        />
        <input
          className="absolute inset-0 w-0 h-0 opacity-0 cursor-pointer"
          id={id}
          name={id}
          type="file"
          accept={
            allowedFields
              ? `.${allowedFields.join(",.")}`
              : `.${APP_CONFIG.DOCUMENT_UPLOAD.ALLOWED_FILE_EXT.join(",.")}`
          } // accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,"
          onChange={handleFileChange}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative ${
        floatingError ? ` pb-4` : ""
      } ${wrapperClass} ${customWrapperClass}`}
    >
      <label
        htmlFor={id}
        className={`block text-sm font-display font-semibold ${labelClass}`}
      >
        {label}
      </label>

      <div className="flex flex-row gap-4 justify-between items-center">
        <label
          htmlFor={id}
          className={`text-sm whitespace-nowrap overflow-hidden text-ellipsis ${
            fieldHasError && showErrorValue
              ? "text-field-error-dark"
              : "text-gray-400"
          }`}
        >
          {fileName || t("file_upload.no_file")}
        </label>
        <div
          className={`border rounded-md relative w-36 h-10 flex items-center justify-center cursor-pointer ${
            fieldHasError && showErrorValue
              ? "border-field-error-dark hover:bg-field-error-dark/10"
              : "border-accent-blue-dark hover:bg-accent-blue-paleO66"
          }`}
        >
          <span
            className={`text-sm font-bold ${
              fieldHasError && showErrorValue
                ? "text-field-error-dark"
                : "text-accent-blue-dark"
            }`}
          >
            {t(
              fileName ? "file_upload.file_selected" : "file_upload.select_file"
            )}
          </span>
          <input
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id={id}
            name={id}
            type="file"
            accept={
              allowedFields
                ? `.${allowedFields.join(",.")}`
                : `.${APP_CONFIG.DOCUMENT_UPLOAD.ALLOWED_FILE_EXT.join(",.")}`
            } // accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,"
            onChange={handleFileChange}
            // className={`mt-1 w-full h-11 px-3 rounded-md text-sm shadow-sm enabled:outline-accent-blue-dark focus-visible:outline-4 focus-visible:shadow-none${touched ? " touched" : ""
            //   } ${disabled || readOnly ? "bg-gray-100" : "bg-white"} ${inputClass}${type === 'password' ? ' tracking-widest placeholder-tracking-normal' : ''} ${customInputClass}`}
          />
        </div>
      </div>
      {fieldHasError && showErrorValue && (
        <p
          className={`${
            floatingError ? "absolute" : ""
          } bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
        >
          {error === "VALIDATION_FIELD_FILE_BIG"
            ? t("file_upload.validation.file_size_exceeded", {
                size: APP_CONFIG.DOCUMENT_UPLOAD.MAX_FILE_SIZE_MB,
              })
            : error === "VALIDATION_FIELD_FILE_WRONG_TYPE"
              ? t("file_upload.validation.file_type_not_supported", {
                  extensions:
                    APP_CONFIG.DOCUMENT_UPLOAD.ALLOWED_FILE_EXT.join(", "),
                })
              : error}
        </p>
      )}
    </div>
  );
};
